import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Breadcrumbs,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams, Link } from 'react-router-dom';
import getSystemProvider from '../../../Apollo/queries/getSystemProvider';
import getLetterTemplates from '../../../Apollo/queries/getLetterTemplates';
import createSystemProviderMutation from '../../../Apollo/mutations/createSystemProvider';
import updateSystemProviderMutation from '../../../Apollo/mutations/updateSystemProvider';
import { PagePathname } from '../../../routes/paths';
import { useFormUsageMachine, useFormUsageState } from '../../../features/usage-tracking/hooks/useFormUsage';

export const DISCARD_BUTTON_LABEL = 'Verwerfen und zurück';
export const DISCARD_BUTTON_SX = { textTransform: 'none' } as const;
const NO_LETTER_TEMPLATES_MESSAGE = 'Erstelle zuerst eine Briefvorlage unter Einstellungen -> Vorlagen.';

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Name ist Pflichtfeld').max(100, 'Maximal 100 Zeichen'),
  recipient_email: Yup.string().required('E-Mail ist Pflichtfeld').email('Keine gültige E-Mail-Adresse'),
});

function SystemProviderFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const { data: providerData, loading: loadingProvider } = useQuery(getSystemProvider, {
    variables: { id: Number(id) },
    skip: !isEdit,
    fetchPolicy: 'network-only',
  });

  const { data: templatesData } = useQuery(getLetterTemplates);
  const letterTemplates: { id: number; name: string }[] = templatesData?.getLetterTemplates || [];

  const [createProvider] = useMutation(createSystemProviderMutation);
  const [updateProvider] = useMutation(updateSystemProviderMutation);

  const provider = providerData?.getSystemProvider;

  // RITA-1504: Der Automat entsteht VOR useFormik, damit onSubmit den Ausgang melden kann;
  // die Maske selbst hängt darunter an useFormUsageState. Gebunden an die Kennung aus der Route:
  // ein Datensatzwechsel ohne Abbau der Seite ist eine neue Maskensitzung (KI-Review !727).
  const formUsage = useFormUsageMachine('systemdienstleister.maske', { session: id ?? 'neu' });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: provider?.name || '',
      recipient_email: provider?.recipient_email || '',
      letter_template_id: provider?.letter_template_id ?? '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setServerError(null);
      try {
        const templateId = values.letter_template_id === '' ? null : Number(values.letter_template_id);
        if (isEdit) {
          await updateProvider({
            variables: {
              id: Number(id),
              variables: {
                name: values.name,
                recipient_email: values.recipient_email,
                letter_template_id: templateId,
              },
            },
          });
        } else {
          await createProvider({
            variables: {
              variables: {
                name: values.name,
                recipient_email: values.recipient_email,
                letter_template_id: templateId,
              },
            },
          });
        }
        setSaveSuccess(true);
        formUsage.submitted('ok');
        setTimeout(() => {
          navigate(PagePathname.systemdienstleister);
        }, 800);
      } catch (err: unknown) {
        formUsage.submitted('fail');
        const error = err as { message?: string };
        if (error.message?.includes('DUPLICATE_NAME') || error.message?.includes('existiert bereits')) {
          setServerError('Ein Dienstleister mit diesem Namen existiert bereits');
        } else {
          setServerError(error.message || 'Fehler beim Speichern');
        }
      }
    },
  });

  // Die Maske gilt als geöffnet, sobald sie sichtbar ist — im Bearbeiten-Fall also erst nach dem
  // Laden des Dienstleisters.
  // RITA-1504: Formik erreicht onSubmit bei ungültiger Maske nie — der Prüffehler wäre sonst kein
  // gemeldeter Ausgang (Lücke aus dem Verdrahtungstest zu KI-Review !727). Vor dem Absenden selbst
  // prüfen und nur die NAMEN der ungültigen Felder melden; das Absenden läuft danach unverändert.
  const handleSave = async (): Promise<void> => {
    const errors = await formik.validateForm();
    const fields = Object.keys(errors);
    if (fields.length > 0) formUsage.submitted('fail', fields);
    formik.handleSubmit();
  };

  useFormUsageState('systemdienstleister.maske', formik, {
    active: !(isEdit && loadingProvider),
    machine: formUsage,
  });

  const handleCancel = () => {
    if (formik.dirty) {
      setCancelDialogOpen(true);
    } else {
      navigate(PagePathname.systemdienstleister);
    }
  };

  if (isEdit && loadingProvider) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Laden...</Typography>
      </Box>
    );
  }

  const pageTitle = isEdit ? `${provider?.name} bearbeiten` : 'Neuen Dienstleister anlegen';
  const pageSubtitle = isEdit
    ? 'Daten des Dienstleisters aktualisieren'
    : 'Füge einen neuen externen Systemdienstleister hinzu';
  const TitleIcon = isEdit ? EditIcon : LinkIcon;

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to={PagePathname.einstellung} underline="hover" color="inherit">
          Einstellungen
        </MuiLink>
        <MuiLink component={Link} to={PagePathname.systemdienstleister} underline="hover" color="inherit">
          Systemdienstleister
        </MuiLink>
        <Typography color="text.primary">{isEdit ? provider?.name || 'Bearbeiten' : 'Neuer Dienstleister'}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TitleIcon />
            <Typography variant="h5" fontWeight="bold">
              {pageTitle}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {pageSubtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={handleCancel}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            startIcon={saveSuccess ? <CheckIcon /> : <SaveIcon />}
            onClick={handleSave}
            disabled={formik.isSubmitting}
            sx={{
              bgcolor: saveSuccess ? '#22C55E' : '#4ADE80',
              color: '#000',
              fontWeight: 'bold',
              '&:hover': { bgcolor: '#22C55E' },
            }}
          >
            {saveSuccess ? 'Gespeichert!' : 'Speichern'}
          </Button>
        </Box>
      </Box>

      {serverError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setServerError(null)}>
          {serverError}
        </Alert>
      )}

      {/* Form Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {/* STAMMDATEN */}
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 2, letterSpacing: 1.5 }}>
          Stammdaten
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
          DIENSTLEISTER-NAME <span style={{ color: '#EF4444' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          name="name"
          placeholder="z.B. Bistro, Paxlounge, Explorer..."
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          inputProps={{ maxLength: 100 }}
          sx={{ mb: 3 }}
        />

        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
          EMPFÄNGER-E-MAIL <span style={{ color: '#EF4444' }}>*</span>
        </Typography>
        <TextField
          fullWidth
          name="recipient_email"
          placeholder="z.B. bestellungen@dienstleister.de"
          value={formik.values.recipient_email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.recipient_email && Boolean(formik.errors.recipient_email)}
          helperText={formik.touched.recipient_email && formik.errors.recipient_email}
          sx={{ mb: 1 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
          An diese Adresse werden automatisierte E-Mails bei Beantragung gesendet.
        </Typography>

        {/* BRIEFVORLAGE */}
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: 'block', mb: 2, mt: 2, letterSpacing: 1.5 }}
        >
          Briefvorlage
        </Typography>

        {letterTemplates.length === 0 ? (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" component={Link} to={PagePathname.briefvorlagen}>
                Vorlagen verwalten
              </Button>
            }
          >
            {NO_LETTER_TEMPLATES_MESSAGE}
          </Alert>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              BRIEFVORLAGE ZUORDNEN
            </Typography>
            <TextField
              fullWidth
              select
              name="letter_template_id"
              value={formik.values.letter_template_id}
              onChange={formik.handleChange}
              sx={{ mb: 1 }}
            >
              <MenuItem value="">
                {/* In edit-Modus: Template-Auswahl ändert is_active NICHT (nur setSystemProviderActive tut das).
                    "inaktiv" wäre daher irreführend. In new-Modus bestimmt das Template den Anfangsstatus. */}
                <em>{isEdit ? 'Keine Vorlage zugeordnet' : 'Keine Vorlage (Dienstleister wird inaktiv angelegt)'}</em>
              </MenuItem>
              {letterTemplates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Die zugeordnete Briefvorlage wird beim Beantragen eines Systemzugangs als E-Mail-Vorlage verwendet.
            </Typography>
          </>
        )}

        {/* STATUS (read-only — in edit mode shows DB value from is_active, in new mode derived from letter_template_id as preview) */}
        {(() => {
          const isStatusActive = isEdit ? Boolean(provider?.is_active) : Boolean(formik.values.letter_template_id);
          return (
            <>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, mt: 2, letterSpacing: 1.5 }}
              >
                Status
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: isStatusActive ? '1px solid rgba(34, 197, 94, 0.4)' : '1px solid rgba(156, 163, 175, 0.4)',
                  bgcolor: isStatusActive ? 'rgba(34, 197, 94, 0.06)' : 'rgba(156, 163, 175, 0.06)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    component="span"
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: isStatusActive ? '#22C55E' : '#9CA3AF',
                    }}
                  />
                  <Typography fontWeight="bold" color={isStatusActive ? '#22C55E' : '#9CA3AF'}>
                    {isStatusActive ? 'Aktiv' : 'Inaktiv'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                  {(() => {
                    if (isStatusActive) return 'Briefvorlage zugeordnet – Dienstleister ist bereit für Beantragungen';
                    if (!isEdit) return 'Keine Briefvorlage ausgewählt – Dienstleister wird inaktiv angelegt';
                    // formik.values (live Formular-Stand) statt provider?.letter_template_id
                    // (veraltete Server-Props) — sonst zeigte das Formular nach Vorlage-Auswahl
                    // den falschen Hinweis (RITA-1413 P2 Review-Fix).
                    if (formik.values.letter_template_id) return 'Manuell deaktiviert – über die Liste reaktivierbar';
                    return 'Keine Briefvorlage zugeordnet – erst Briefvorlage wählen, dann reaktivieren';
                  })()}
                </Typography>
              </Box>
            </>
          );
        })()}
      </Paper>

      {/* Info Box */}
      <Paper
        sx={{
          p: 2,
          borderLeft: '4px solid #3B82F6',
          bgcolor: 'rgba(59, 130, 246, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <InfoIcon sx={{ color: '#3B82F6', fontSize: 20 }} />
          <Typography variant="body2" color="#3B82F6" fontWeight="bold">
            Hinweis
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Nach dem Anlegen erscheint dieser Dienstleister automatisch im Tab &bdquo;Systeme&ldquo; jedes Beraterprofils.
          Sobald eine Briefvorlage zugeordnet ist, können Zugänge beantragt werden.
        </Typography>
      </Paper>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Bearbeitung abbrechen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchtest du die Bearbeitung abbrechen? Alle nicht gespeicherten Eingaben gehen verloren.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setCancelDialogOpen(false)}
            sx={{
              bgcolor: '#4ADE80',
              color: '#000',
              '&:hover': { bgcolor: '#22C55E' },
            }}
          >
            Weiterbearbeiten
          </Button>
          <Button variant="outlined" sx={DISCARD_BUTTON_SX} onClick={() => navigate(PagePathname.systemdienstleister)}>
            {DISCARD_BUTTON_LABEL}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SystemProviderFormPage;
